import React, { useState } from "react";
import { Form, Row, Col, Button, Input, Collapse, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { ReactSVG } from "react-svg";
import { useHistory } from "react-router-dom";
import AddressForm from "./form/AddressForm";
import PackageList from "./PackageList";
import PackageListDrop from "./PackageListDrop";
import MapImportPreview from "modules/client/routes/components/maps/MapImportPreview";
import ConfirmRoutePage from "modules/client/active-orders/pages/ConfirmRoutePage";
import ImportLocations from "modules/client/routes/components/exports";
import ServiceStep from "./steps/ServiceStep";
import StopOrderingSelect from "./StopOrderingSelect";
import ReturnProcessSelect from "./return-process/ReturnProcessSelect";
import { DEFAULT_STOP_ORDER_MODE } from "../stopOrderModes";
import "./StepContent.scss";

/**
 * StepContent - Renders the appropriate step content based on currentStep
 * NO COLLAPSIBLE/EXPANDABLE UI - just renders the content for the current step
 * Handles both Admin and Client flows
 */
const StepContent = ({
  currentStep,
  isAdmin,
  clientId,

  // Service Selection (Step 1)
  flexioServices,
  pickupDate,
  setPickupDate,
  clientType,
  routes,
  setRoutes,

  // Locations form (Step 2)
  form,
  pickups,
  dropoffs,
  packages,
  onValuesChange,
  setShowImport,
  onChangePickupAddress,
  onChangePickupLocationSearch,
  checkWarehousesForPickup,
  onChangeTime,
  imagesAddressInfo,
  setImagesAddressInfo,
  addPickup,
  onChangeDropoffAddress,
  onChangeDropoffLocationSearch,
  getPackForDrop,
  openSelectedPack,
  isAllPackage,
  addDropoff,
  openPackCreateUpdate,
  removePack,
  warehousesByPickup,
  idErrors,
  pickupsInit,
  dropoffsInit,

  // Map confirmation (Step 3)
  // uses pickups, dropoffs, form from above

  // Route optimization (Step 4)
  type,
  isConfirmRoute,
  packageTypeList,

  // Stop ordering (Step 2 / admin Step 3)
  stopOrderMode = DEFAULT_STOP_ORDER_MODE,
  setStopOrderMode,
  preferredNoOfStops,

  // Return process (Step 2 / admin Step 3): { returnToPickupEnabled, returnOnFailedEnabled,
  // defaults, settings, loading, clientMissing, onChange }
  returnProcess
}) => {
  const history = useHistory();
  // State for managing which accordion items are expanded
  const [pickupActiveKeys, setPickupActiveKeys] = useState([]);
  const [dropoffActiveKeys, setDropoffActiveKeys] = useState([]);

  // STEP 1: Locations (for admin)
  if (currentStep === 1 && isAdmin) {
    // Admin Step 1: Same as client - locations entry
    // Render the locations form
    return (
      <section className="step-content step-locations">
        <div className="step-content-wrapper">
          <h3>Step 1: Locations</h3>
          <p className="step-description">
            Upload or manually enter address data to start planning your route.
          </p>

          <Row gutter={10} className="mb-3">
            <Col md={5}>
              <Button
                className="svg-icon d-flex flex-row action-button"
                onClick={() => history.push("/import-file")}
              >
                Upload a file .csv, .xlsx
              </Button>
            </Col>
          </Row>

          <Form
            onValuesChange={onValuesChange}
            scrollToFirstError={true}
            layout="vertical"
            form={form}
            hideRequiredMark
            colon={false}
            initialValues={{
              pickups: pickupsInit,
              dropoffs: dropoffsInit
            }}
            className="mt-2"
          >
            {/* PICKUPS */}
            <Form.List name="pickups">
              {(fields, { add, remove }) => (
                <div className="pickups block">
                  <h4 className="locations-subheader">Pickup Locations</h4>
                  {fields.map(({ key, name, fieldKey, isListField }, index) => {
                    const pickupData = form.getFieldValue("pickups")?.[index];
                    return (
                      <div key={pickupData?.id} className="location-form-section">
                        <div className="location-section-header">
                          <span className="location-index">Pickup {index + 1}</span>
                          {fields.length > 1 && (
                            <Button size="small" danger onClick={() => remove(index)}>
                              Remove
                            </Button>
                          )}
                        </div>

                        <AddressForm
                          stepType="pickups"
                          setAddress={onChangePickupAddress}
                          handleLocationNameUpdate={onChangePickupLocationSearch}
                          onLocationSelected={(savedLocationId) =>
                            checkWarehousesForPickup(pickupData?.id, savedLocationId)
                          }
                          address={pickupData?.address}
                          orderData={pickupData}
                          id={pickupData?.id}
                          keyValue={key}
                          nameValue={name}
                          fieldKey={fieldKey}
                          isListField={isListField}
                          form={form}
                          onChangeTime={onChangeTime}
                          imagesAddressInfo={imagesAddressInfo}
                          setImagesAddressInfo={setImagesAddressInfo}
                        />

                        <PackageList
                          key={`${key}-pickup-pack`}
                          packages={packages.filter(
                            (pa) => pa.pickupId === pickupData?.id
                          )}
                          pickupId={pickupData?.id}
                          editPack={(pack) =>
                            openPackCreateUpdate("edit", pickupData?.id, "pickup", pack)
                          }
                          addPack={() => {
                            openPackCreateUpdate(
                              "new",
                              pickupData?.id,
                              "pickup",
                              null,
                              false,
                              true
                            );
                          }}
                          removePack={removePack}
                          hasWarehouses={warehousesByPickup[pickupData?.id]?.length > 0}
                          addFromWarehouse={() => {
                            openPackCreateUpdate(
                              "new",
                              pickupData?.id,
                              "pickup",
                              null,
                              true
                            );
                          }}
                        />
                      </div>
                    );
                  })}

                  <Button
                    size="large"
                    className="add-all justify-content-center"
                    onClick={() => add(addPickup())}
                  >
                    <ReactSVG src="/icons/plus.svg" className="react-icon mr-4" />
                    Add Another Pickup Location
                  </Button>
                </div>
              )}
            </Form.List>

            {/* DROPOFFS */}
            <Form.List name="dropoffs">
              {(fieldDropoffs, { add, remove }) => (
                <div className="dropoffs block">
                  <h4 className="locations-subheader">Dropoff Locations</h4>
                  {fieldDropoffs.map(({ key, name, fieldKey, isListField }, index) => {
                    const dropoffData = form.getFieldValue("dropoffs")?.[index];
                    return (
                      <div key={dropoffData?.id} className="location-form-section">
                        <div className="location-section-header">
                          <span className="location-index">Dropoff {index + 1}</span>
                          {fieldDropoffs.length > 1 && (
                            <Button size="small" danger onClick={() => remove(index)}>
                              Remove
                            </Button>
                          )}
                        </div>

                        <AddressForm
                          isAdmin={isAdmin}
                          clientId={clientId}
                          stepType="dropoffs"
                          setAddress={onChangeDropoffAddress}
                          handleLocationNameUpdate={onChangeDropoffLocationSearch}
                          address={dropoffData?.address}
                          orderData={dropoffData}
                          id={dropoffData?.id}
                          keyValue={key}
                          nameValue={name}
                          fieldKey={fieldKey}
                          isListField={isListField}
                          form={form}
                          onChangeTime={onChangeTime}
                          imagesAddressInfo={imagesAddressInfo}
                          setImagesAddressInfo={setImagesAddressInfo}
                        />
                        <PackageListDrop
                          id={dropoffData?.id}
                          packages={getPackForDrop(dropoffData?.id)}
                          selectedPack={() => openSelectedPack(dropoffData?.id)}
                          isAll={isAllPackage}
                        />
                      </div>
                    );
                  })}

                  <Button
                    size="large"
                    className="add-all justify-content-center"
                    onClick={() => add(addDropoff())}
                  >
                    <ReactSVG src="/icons/plus.svg" className="react-icon mr-4" />
                    Add Another Dropoff Location
                  </Button>
                </div>
              )}
            </Form.List>
          </Form>
        </div>
      </section>
    );
  }

  // STEP 1 or 2: Locations Form (Client Step 1 OR Admin Step 2)
  if ((currentStep === 1 && !isAdmin) || (currentStep === 2 && isAdmin)) {
    return (
      <section className="step-content step-locations">
        <div className="step-content-wrapper">
          <h3>{isAdmin ? "Step 2: Locations" : "Step 1: Locations"}</h3>
          <p className="step-description">
            Upload or manually enter address data to start planning your route.
          </p>

          <Row gutter={10} className="mb-3">
            <Col md={5}>
              <Button
                className="svg-icon d-flex flex-row action-button"
                onClick={() => history.push("/import-file")}
              >
                Upload a file .csv, .xlsx
              </Button>
            </Col>
          </Row>

          <Form
            onValuesChange={onValuesChange}
            scrollToFirstError={true}
            layout="vertical"
            form={form}
            hideRequiredMark
            colon={false}
            initialValues={{
              pickups: pickupsInit,
              dropoffs: dropoffsInit
            }}
            className="mt-2"
          >
            {/* PICKUPS - With Nested Accordion */}
            <Form.List name="pickups">
              {(fields, { add, remove }) => (
                <Collapse
                  className="main-sections-accordion pickups-main-section"
                  onChange={(keys) => {
                    if (!keys.includes("pickups-main")) {
                      setPickupActiveKeys([]);
                    }
                  }}
                >
                  <Collapse.Panel
                    key="pickups-main"
                    header={
                      <div
                        className="main-accordion-header"
                        style={{ margin: 0, padding: 0 }}
                      >
                        <span className="main-section-label">📍 Pickup Locations</span>
                        <span className="item-count">{fields.length}</span>
                      </div>
                    }
                  >
                    <div className="pickups block">
                      <Collapse
                        className="pickup-main-accordion"
                        accordion
                        activeKey={pickupActiveKeys}
                        onChange={(keys) => {
                          setPickupActiveKeys(keys);
                        }}
                      >
                        {fields.map(({ key, name, fieldKey, isListField }, index) => {
                          const pickupData = form.getFieldValue("pickups")?.[index];
                          const pickupTitle =
                            pickupData?.location_name || `Pickup ${index + 1}`;
                          const contactName = pickupData?.contact_name || "";
                          const hasData =
                            pickupTitle !== `Pickup ${index + 1}` || contactName;
                          return (
                            <Collapse.Panel
                              key={pickupData?.id}
                              header={
                                <div
                                  className="pickup-accordion-header"
                                  style={{ margin: 0, padding: 0 }}
                                >
                                  <span className="pickup-label">Pickup {index + 1}</span>
                                  <div className="pickup-details">
                                    {hasData ? (
                                      <span className="pickup-location">
                                        {pickupTitle}
                                      </span>
                                    ) : (
                                      <Tooltip title="Missing location or contact name">
                                        <QuestionCircleOutlined className="pickup-missing-icon" />
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              }
                              extra={
                                fields.length > 1 && (
                                  <Button
                                    size="small"
                                    danger
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      remove(index);
                                    }}
                                  >
                                    Remove
                                  </Button>
                                )
                              }
                            >
                              <div className="pickup-content">
                                <AddressForm
                                  stepType="pickups"
                                  setAddress={onChangePickupAddress}
                                  handleLocationNameUpdate={onChangePickupLocationSearch}
                                  onLocationSelected={(savedLocationId) =>
                                    checkWarehousesForPickup(
                                      pickupData?.id,
                                      savedLocationId
                                    )
                                  }
                                  address={pickupData?.address}
                                  orderData={pickupData}
                                  id={pickupData?.id}
                                  keyValue={key}
                                  nameValue={name}
                                  fieldKey={fieldKey}
                                  isListField={isListField}
                                  form={form}
                                  onChangeTime={onChangeTime}
                                  imagesAddressInfo={imagesAddressInfo}
                                  setImagesAddressInfo={setImagesAddressInfo}
                                />

                                <PackageList
                                  key={`${key}-pickup-pack`}
                                  packages={packages.filter(
                                    (pa) => pa.pickupId === pickupData?.id
                                  )}
                                  pickupId={pickupData?.id}
                                  editPack={(pack) =>
                                    openPackCreateUpdate(
                                      "edit",
                                      pickupData?.id,
                                      "pickup",
                                      pack
                                    )
                                  }
                                  addPack={() => {
                                    openPackCreateUpdate(
                                      "new",
                                      pickupData?.id,
                                      "pickup",
                                      null,
                                      false,
                                      true
                                    );
                                  }}
                                  removePack={removePack}
                                  hasWarehouses={
                                    warehousesByPickup[pickupData?.id]?.length > 0
                                  }
                                  addFromWarehouse={() => {
                                    openPackCreateUpdate(
                                      "new",
                                      pickupData?.id,
                                      "pickup",
                                      null,
                                      true
                                    );
                                  }}
                                />
                              </div>
                            </Collapse.Panel>
                          );
                        })}
                      </Collapse>

                      <Button
                        size="large"
                        className="add-all justify-content-center"
                        onClick={() => add(addPickup())}
                      >
                        <ReactSVG src="/icons/plus.svg" className="react-icon mr-4" />
                        Add Another Pickup Location
                      </Button>
                    </div>
                  </Collapse.Panel>
                </Collapse>
              )}
            </Form.List>

            {/* DROPOFFS - With Nested Accordion */}
            <Form.List name="dropoffs">
              {(fieldDropoffs, { add, remove }) => (
                <Collapse
                  className="main-sections-accordion dropoffs-main-section"
                  onChange={(keys) => {
                    if (!keys.includes("dropoffs-main")) {
                      setDropoffActiveKeys([]);
                    }
                  }}
                >
                  <Collapse.Panel
                    key="dropoffs-main"
                    header={
                      <div
                        className="main-accordion-header"
                        style={{ margin: 0, padding: 0 }}
                      >
                        <span className="main-section-label">🏷️ Dropoff Locations</span>
                        <span className="item-count">{fieldDropoffs.length}</span>
                      </div>
                    }
                  >
                    <div className="dropoffs block">
                      <Collapse
                        className="dropoff-main-accordion"
                        accordion
                        activeKey={dropoffActiveKeys}
                        onChange={(keys) => {
                          setDropoffActiveKeys(keys);
                        }}
                      >
                        {fieldDropoffs.map(
                          ({ key, name, fieldKey, isListField }, index) => {
                            const dropoffData = form.getFieldValue("dropoffs")?.[index];
                            const dropoffTitle =
                              dropoffData?.location_name || `Dropoff ${index + 1}`;
                            const contactName = dropoffData?.contact_name || "";
                            const hasData =
                              dropoffTitle !== `Dropoff ${index + 1}` || contactName;
                            return (
                              <Collapse.Panel
                                key={dropoffData?.id}
                                header={
                                  <div
                                    className="dropoff-accordion-header"
                                    style={{ margin: 0, padding: 0 }}
                                  >
                                    <span className="dropoff-label">
                                      Dropoff {index + 1}
                                    </span>
                                    <div className="dropoff-details">
                                      {hasData ? (
                                        <span className="dropoff-location">
                                          {dropoffTitle}
                                        </span>
                                      ) : (
                                        <Tooltip title="Missing location or contact name">
                                          <QuestionCircleOutlined className="dropoff-missing-icon" />
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                }
                                extra={
                                  fieldDropoffs.length > 1 && (
                                    <Button
                                      size="small"
                                      danger
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        remove(index);
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  )
                                }
                              >
                                <div className="dropoff-content">
                                  <AddressForm
                                    isAdmin={isAdmin}
                                    clientId={clientId}
                                    stepType="dropoffs"
                                    setAddress={onChangeDropoffAddress}
                                    handleLocationNameUpdate={
                                      onChangeDropoffLocationSearch
                                    }
                                    address={dropoffData?.address}
                                    orderData={dropoffData}
                                    id={dropoffData?.id}
                                    keyValue={key}
                                    nameValue={name}
                                    fieldKey={fieldKey}
                                    isListField={isListField}
                                    form={form}
                                    onChangeTime={onChangeTime}
                                    imagesAddressInfo={imagesAddressInfo}
                                    setImagesAddressInfo={setImagesAddressInfo}
                                  />
                                  <PackageListDrop
                                    id={dropoffData?.id}
                                    packages={getPackForDrop(dropoffData?.id)}
                                    selectedPack={() => openSelectedPack(dropoffData?.id)}
                                    isAll={isAllPackage}
                                  />
                                </div>
                              </Collapse.Panel>
                            );
                          }
                        )}
                      </Collapse>

                      <Button
                        size="large"
                        className="add-all justify-content-center"
                        onClick={() => add(addDropoff())}
                      >
                        <ReactSVG src="/icons/plus.svg" className="react-icon mr-4" />
                        Add Another Dropoff Location
                      </Button>
                    </div>
                  </Collapse.Panel>
                </Collapse>
              )}
            </Form.List>
          </Form>
        </div>
      </section>
    );
  }

  // STEP 2 or 3: Confirm Locations Map (Client Step 2 OR Admin Step 3)
  if ((currentStep === 2 && !isAdmin) || (currentStep === 3 && isAdmin)) {
    return (
      <section className="step-content step-confirm-locations">
        <div className="step-content-wrapper">
          <h3>{isAdmin ? "Step 3: Confirm Locations" : "Step 2: Confirm Locations"}</h3>
          <p className="step-description">
            Review and confirm your pickup and drop-off locations to ensure accurate
            delivery.
          </p>

          <StopOrderingSelect
            stopOrderMode={stopOrderMode}
            setStopOrderMode={setStopOrderMode}
            dropoffs={dropoffs}
            preferredNoOfStops={preferredNoOfStops}
          />

          {returnProcess && <ReturnProcessSelect {...returnProcess} />}

          <div className="location-map">
            <div className="pin-map">
              <MapImportPreview
                routes={[...pickups, ...dropoffs]}
                isLocation={true}
                title=""
              />
            </div>
            <Row gutter={30} className="mt-3">
              <Col className="d-flex">
                <img
                  src="/icons/map-pinpoint-blue.svg"
                  style={{ width: 30 }}
                  height={30}
                  alt="pickup"
                />
                <p className="pt-1 pl-3 pr-3 m-0 pin-legend-text">Pickup Location</p>
              </Col>
              <Col className="d-flex">
                <img
                  src="/icons/map-pinpoint-orange.svg"
                  style={{ width: 30 }}
                  height={30}
                  alt="dropoff"
                />
                <p className="pt-1 pl-3 pr-3 m-0 pin-legend-text">Dropoff Location</p>
              </Col>
            </Row>
          </div>

          <Row gutter={20} className="mt-4">
            <Col md={12}>
              <p className="location-title">Pickup Locations</p>
              {pickups.map((pickup, pickupIdx) => (
                <div key={pickupIdx + 1} className="confirm-location-wrapper">
                  <span className="location-num location-num__pick">{pickupIdx + 1}</span>
                  <Input
                    value={pickup?.address?.address}
                    className="location-input ml-2"
                    readOnly
                  />
                </div>
              ))}
            </Col>
            <Col md={12}>
              <p className="location-title">Dropoff Locations</p>
              {dropoffs.map((dropoff, dropIdx) => (
                <div key={dropIdx + 1} className="confirm-location-wrapper">
                  <span className="location-num location-num__drop">{dropIdx + 1}</span>
                  <Input
                    value={dropoff?.address?.address}
                    className="location-input ml-2"
                    readOnly
                  />
                </div>
              ))}
            </Col>
          </Row>
        </div>
      </section>
    );
  }

  // STEP 3 or 4: Route Optimization (Client Step 3 OR Admin Step 4)
  if ((currentStep === 3 && !isAdmin) || (currentStep === 4 && isAdmin)) {
    return (
      <section className="step-content step-route-optimization">
        <div className="step-content-wrapper">
          <h3>
            {isAdmin
              ? "Step 4: Route Optimization & Service Selection"
              : "Step 3: Route Optimization"}
          </h3>
          <p className="step-description">
            Streamline deliveries with Flexio&apos;s smart route optimization, saving time
            and cutting costs.
          </p>

          {!isConfirmRoute && (
            <ConfirmRoutePage
              isAdmin={isAdmin}
              redirect={false}
              handleConfirmState={() => {}}
            />
          )}
        </div>
      </section>
    );
  }

  // STEP 4 or 5: Service Selection (Client Step 4 OR Admin Step 5)
  if ((currentStep === 4 && !isAdmin) || (currentStep === 5 && isAdmin)) {
    return (
      <section className="step-content step-service-selection">
        <div className="step-content-wrapper">
          <ServiceStep
            flexioServices={flexioServices}
            pickupDate={pickupDate}
            setPickupDate={setPickupDate}
            routes={routes}
            setRoutes={setRoutes}
            estimatePriceOrganization={clientType === "organization"}
            clientId={clientId}
          />
        </div>
      </section>
    );
  }

  // Fallback - shouldn't reach here
  return null;
};

export default StepContent;
